import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import useModules from "../hooks/useModules";
import { useAuth } from "../contexts/AuthContext";
import { canAccessModuleId } from "../lib/rbac";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import DateInput from "./DateInput";
import AttendanceConflictsDialog from "./AttendanceConflictsDialog";
import ModuleIdChip from "./ModuleIdChip";
import ProjectPicker from "./ProjectPicker";

const todayIso = () => new Date().toISOString().slice(0, 10);

const NO_PROJECT = "__none__";

// Compute a default row shape. We used to freeze `work_date: todayIso()` as a
// top-level constant which meant the default silently went stale after a tab
// sat open across midnight AND ignored whatever range the user had chosen in
// the parent filters. Now we compute the seed each time the dialog opens and
// prefer the parent's `defaultWorkDate` — that's what the user is actually
// looking at.
const seedForm = (workDate) => ({
    employee_id: "",
    project_id: null,
    work_date: workDate || todayIso(),
    clock_in: "07:30",
    clock_out: "17:00",
    // Iter50-followup — explicit status default. User choice wins over the
    // shift-rule computed classification (see server-side PATCH after the
    // paired events post).
    status: "present",
    notes: "",
});

// Iter53 — status options now include Paid Leave + Unpaid Leave (replacing
// the ambiguous 'Leave'). Mirror the same list in BulkAttendanceDialog.
const STATUS_OPTIONS = [
    { value: "present",       label: "Present" },
    { value: "absent",        label: "Absent"  },
    { value: "paid_leave",    label: "Paid Leave" },
    { value: "unpaid_leave",  label: "Unpaid Leave" },
    { value: "holiday",       label: "Holiday" },
    { value: "weekend",       label: "Weekend" },
    { value: "late",          label: "Late"    },
];

export default function ManualAttendanceDialog({ open, onOpenChange, onSaved, defaultModuleId, defaultWorkDate }) {
    const { modules } = useModules();
    const { user } = useAuth();
    const [form, setForm] = useState(() => seedForm(defaultWorkDate));
    const [moduleId, setModuleId] = useState("");
    const [employees, setEmployees] = useState([]);
    const [projects, setProjects] = useState([]);
    const [search, setSearch] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [loadingEmps, setLoadingEmps] = useState(false);

    // Iter50 — Date-range mode. Single day keeps the exact original write path
    // (paired /attendance/events calls). Range fans out via the new
    // /attendance/daily/range endpoint.
    const [rangeMode, setRangeMode] = useState(false);
    const [range, setRange] = useState(() => ({
        date_from: defaultWorkDate || todayIso(),
        date_to: defaultWorkDate || todayIso(),
        skip_weekends: false,
        skip_holidays: false,
    }));
    const [rangeSummary, setRangeSummary] = useState({ working_days: 0, total_rows: 0 });
    const [rangePreviewing, setRangePreviewing] = useState(false);
    // Iter60.p22 — debounce the employee search so we hit the backend
    // `search` param (which covers full_name / employee_code /
    // module_employee_id / biometric_user_id / national_id / phone)
    // instead of filtering only the preloaded 200. Fixes "typing EMP-4459
    // returns nothing" for orgs with >200 active employees.
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);
    // Iter52 — Conflicts pre-save state. When a save would collide with
    // existing rows, we surface a Conflicts Dialog and only run the actual
    // save after the user picks per-row resolutions.
    const [conflictsState, setConflictsState] = useState({
        open: false,
        conflicts: [],
        cleanCount: 0,
        runSave: null,  // (resolutions) => Promise<void>
    });

    // Modules user is allowed to act on
    const allowedModules = useMemo(
        () => modules.filter((m) => canAccessModuleId(user, m.id) && m.active !== false),
        [modules, user],
    );

    useEffect(() => {
        if (!open) return;
        const wd = defaultWorkDate || todayIso();
        setForm(seedForm(wd));
        setSearch("");
        setRangeMode(false);
        setRange({
            date_from: wd,
            date_to: wd,
            skip_weekends: false,
            skip_holidays: false,
        });
        setRangeSummary({ working_days: 0, total_rows: 0 });
        const initial = defaultModuleId && canAccessModuleId(user, defaultModuleId)
            ? defaultModuleId
            : allowedModules[0]?.id || "";
        setModuleId(initial);
    }, [open, defaultModuleId, defaultWorkDate, allowedModules, user]);

    // Load employees + projects in the selected module. Employees refetch
    // on module change AND on debounced search change (Iter60.p22); projects
    // only refetch on module change since they're not searched here.
    useEffect(() => {
        if (!open || !moduleId) {
            setProjects([]);
            return;
        }
        let alive = true;
        api.get("/projects", { params: { module_id: moduleId, active: true } })
            .then((p) => {
                if (!alive) return;
                setProjects(Array.isArray(p.data) ? p.data : (p.data?.items || []));
            })
            .catch(() => { if (alive) setProjects([]); });
        return () => { alive = false; };
    }, [open, moduleId]);

    useEffect(() => {
        if (!open || !moduleId) {
            setEmployees([]);
            return;
        }
        let alive = true;
        setLoadingEmps(true);
        const params = { module_id: moduleId, status: "active", page_size: 200 };
        if (debouncedSearch) params.search = debouncedSearch;
        api.get("/employees", { params })
            .then((e) => {
                if (!alive) return;
                setEmployees(e.data.items || []);
            })
            .catch((e) => toast.error(formatApiError(e)))
            .finally(() => { if (alive) setLoadingEmps(false); });
        return () => { alive = false; };
    }, [open, moduleId, debouncedSearch]);

    // Iter60.p22 — the picker shows exactly what the backend returned.
    // Keep the memo shape for compatibility with the existing render.
    const filteredEmps = employees;

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Iter51 P0 fix — TZ-invariant event_time construction.
    //
    // Old code was `new Date(\`${date}T${hm}:00\`).toISOString()`. That reads
    // the string as LOCAL wall-clock (per ECMAScript spec for ISO datetimes
    // without an offset) and emits UTC — which means for a browser west of
    // UTC-6.5 a late clock-out like 17:00 gets shifted onto the NEXT UTC day.
    // The server derives work_date from event_time's UTC date, so the daily
    // row lands on filter_date + 1 for those clients (P0 prod bug reported by
    // Somalia users; reproduced by any western-TZ client with late clock-outs).
    //
    // Fix: send the wall-clock string verbatim, no timezone conversion. The
    // server treats naive ISO as UTC for downstream math (which preserves
    // durations / late-minutes since both endpoints use the same convention)
    // and, more importantly, uses the client-supplied `work_date` field as
    // the AUTHORITATIVE key for recompute — decoupling storage-date from
    // event-time's UTC date entirely.
    const buildIso = (date, hm) => `${date}T${hm}:00`;

    // Iter50 — Preview the number of rows we'd create in range mode. Ping the
    // backend `.../range?dry_run=true` variant which returns counts without
    // writing anything. Debounce lightly by depending on the range/toggles.
    const rangeInvalid = rangeMode && range.date_to < range.date_from;
    useEffect(() => {
        if (!open || !rangeMode || !form.employee_id || rangeInvalid) {
            setRangeSummary({ working_days: 0, total_rows: 0 });
            return;
        }
        let alive = true;
        setRangePreviewing(true);
        api.post("/attendance/daily/range", {
            employee_ids: [form.employee_id],
            date_from: range.date_from,
            date_to: range.date_to,
            skip_weekends: range.skip_weekends,
            skip_holidays: range.skip_holidays,
            check_in_time: form.clock_in || null,
            check_out_time: form.clock_out || null,
            status: form.status || "present",
            project_id: form.project_id || null,
            notes: form.notes?.trim() || null,
            dry_run: true,
        }).then(({ data }) => {
            if (!alive) return;
            setRangeSummary({
                working_days: data.working_days ?? 0,
                total_rows: data.would_create ?? 0,
                skipped_weekends: data.skipped_weekends ?? 0,
                skipped_holidays: data.skipped_holidays ?? 0,
                skipped_existing: data.skipped_existing ?? 0,
            });
        }).catch(() => {
            if (alive) setRangeSummary({ working_days: 0, total_rows: 0 });
        }).finally(() => { if (alive) setRangePreviewing(false); });
        return () => { alive = false; };
    }, [open, rangeMode, form.employee_id, range.date_from, range.date_to, range.skip_weekends, range.skip_holidays, form.clock_in, form.clock_out, form.status, rangeInvalid]);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.employee_id) { toast.error("Pick an employee"); return; }
        if (!form.clock_in && !form.clock_out) {
            toast.error("Provide at least clock in or clock out"); return;
        }
        if (form.clock_in && form.clock_out && form.clock_out <= form.clock_in) {
            toast.error("Clock out must be after clock in"); return;
        }
        if (rangeMode) {
            if (rangeInvalid) { toast.error("End date must be on or after start date."); return; }
        }
        setSubmitting(true);
        try {
            // Iter52 — Preflight conflict check.
            const checkBody = rangeMode
                ? {
                    mode: "range",
                    employee_ids: [form.employee_id],
                    date_from: range.date_from,
                    date_to: range.date_to,
                    skip_weekends: range.skip_weekends,
                    skip_holidays: range.skip_holidays,
                }
                : {
                    mode: "manual",
                    employee_ids: [form.employee_id],
                    date: form.work_date,
                };
            const check = await api.post("/attendance/conflicts/check", checkBody);
            const conflicts = check.data?.conflicts || [];
            if (conflicts.length > 0) {
                // Defer the actual save until the user picks resolutions.
                setConflictsState({
                    open: true,
                    conflicts,
                    cleanCount: (check.data?.clean_employees || []).length * (rangeMode ? (check.data?.dates_evaluated?.length || 1) : 1)
                        - conflicts.length + (check.data?.clean_employees?.length ? 0 : 0),
                    runSave: async (resolutions) => {
                        setSubmitting(true);
                        try {
                            await doActualSave(resolutions);
                        } finally { setSubmitting(false); }
                    },
                });
                return;
            }
            await doActualSave([]);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            // Iter60.p19 — ALWAYS reset the button state, whether the save
            // succeeded, failed, or short-circuited into the conflicts flow.
            // The previous code only reset on the conflicts + error paths,
            // so a successful save left `submitting=true` for next open.
            setSubmitting(false);
        }
    };

    const doActualSave = async (resolutions) => {
        if (rangeMode) {
            const { data } = await api.post("/attendance/daily/range", {
                employee_ids: [form.employee_id],
                date_from: range.date_from,
                date_to: range.date_to,
                skip_weekends: range.skip_weekends,
                skip_holidays: range.skip_holidays,
                check_in_time: form.clock_in || null,
                check_out_time: form.clock_out || null,
                status: form.status || "present",
                project_id: form.project_id || null,
                notes: form.notes?.trim() || null,
                resolutions,
            });
            const created = data?.created ?? 0;
            const overwrote = data?.overwrote ?? 0;
            const deleted = data?.deleted_and_recreated ?? 0;
            const skippedExisting = data?.skipped_existing ?? 0;
            const total = created + overwrote + deleted;
            if (total > 0) {
                toast.success(
                    skippedExisting > 0
                        ? `Wrote ${total} row${total === 1 ? "" : "s"} (created ${created}, overwrote ${overwrote}, replaced ${deleted}). Skipped ${skippedExisting} existing.`
                        : `Wrote ${total} row${total === 1 ? "" : "s"}.`,
                );
            } else if (skippedExisting > 0) {
                toast.warning(`No new rows. ${skippedExisting} already exist for that range.`);
            } else {
                toast.warning("No rows created — check the range and toggles.");
            }
            onOpenChange(false);
            onSaved?.({ createdRange: { from: range.date_from, to: range.date_to, created: total } });
            return;
        }
        const common = {
            employee_id: form.employee_id,
            source: "manual",
            verification_method: "manual_entry",
            project_id: form.project_id || null,
            notes: form.notes?.trim() || null,
            work_date: form.work_date,
        };
        // Manual mode with resolutions: if there's a resolution for this
        // employee/date, honour it. Otherwise fall through to the classic
        // events-post path.
        const res = (resolutions || []).find((r) => r.employee_id === form.employee_id && r.work_date === form.work_date);
        if (res && res.action === "skip") {
            toast.info("Kept the existing record — no changes made.");
            onOpenChange(false);
            return;
        }
        if (res && (res.action === "overwrite" || res.action === "delete_and_recreate")) {
            // Route via /bulk which supports overwrite/delete_and_recreate cleanly.
            await api.post("/attendance/bulk", {
                employee_ids: [form.employee_id],
                date: form.work_date,
                status: form.status || "present",
                project_id: form.project_id || null,
                clock_in: form.clock_in || null,
                clock_out: form.clock_out || null,
                notes: form.notes?.trim() || null,
                resolutions: [{ employee_id: form.employee_id, work_date: form.work_date, action: res.action }],
            });
            toast.success(res.action === "overwrite" ? "Attendance overwritten." : "Existing record replaced.");
            onOpenChange(false);
            onSaved?.({ createdRange: { from: form.work_date, to: form.work_date, created: 1 } });
            return;
        }
        if (form.clock_in) {
            await api.post("/attendance/events", {
                ...common,
                event_type: "clock_in",
                event_time: buildIso(form.work_date, form.clock_in),
            });
        }
        if (form.clock_out) {
            await api.post("/attendance/events", {
                ...common,
                event_type: "clock_out",
                event_time: buildIso(form.work_date, form.clock_out),
            });
        }
        // Iter50-followup — user's explicit status choice overrides the
        // shift-rule computed classification.
        if (form.status) {
            try {
                const dailyRes = await api.get("/attendance/daily", {
                    params: {
                        employee_id: form.employee_id,
                        date_from: form.work_date,
                        date_to: form.work_date,
                        page_size: 5,
                    },
                });
                const rows = Array.isArray(dailyRes.data)
                    ? dailyRes.data
                    : (dailyRes.data?.items || []);
                const daily = rows.find(
                    (r) => r.employee_id === form.employee_id && r.work_date === form.work_date,
                );
                if (daily && daily.status !== form.status) {
                    await api.patch(`/attendance/daily/${daily.id}`, { status: form.status });
                }
            } catch (patchErr) {
                // eslint-disable-next-line no-console
                console.warn("Failed to override manual status:", patchErr);
            }
        }
        toast.success("Manual attendance saved");
        onOpenChange(false);
        onSaved?.({ createdRange: { from: form.work_date, to: form.work_date, created: 1 } });
    };

    const selectedEmp = employees.find((e) => e.id === form.employee_id);

    return (
        <>
        <Dialog open={open && !conflictsState.open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-lg max-h-[90vh] flex flex-col p-0 gap-0"
                data-testid="manual-attendance-dialog"
            >
                <DialogHeader className="px-6 pt-6 pb-2 flex-none">
                    <DialogTitle>Add Manual Attendance</DialogTitle>
                    <DialogDescription>
                        Record a clock-in and/or clock-out on behalf of an employee. The day&apos;s
                        attendance summary will be recomputed automatically.
                    </DialogDescription>
                </DialogHeader>
                <form
                    onSubmit={onSubmit}
                    className="flex flex-col flex-1 min-h-0"
                    data-testid="manual-attendance-form"
                >
                    <div className="flex-1 overflow-y-auto px-6 pt-2 pb-4 space-y-4">
                    {/* Iter50 — Single day vs Date range mode toggle */}
                    <ModeToggle
                        value={rangeMode ? "range" : "single"}
                        onChange={(v) => setRangeMode(v === "range")}
                        testid="manual-att-mode"
                    />

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Module" required>
                            <Select value={moduleId} onValueChange={(v) => { setModuleId(v); set("employee_id", ""); }}>
                                <SelectTrigger data-testid="manual-att-module">
                                    <SelectValue placeholder="Choose module" />
                                </SelectTrigger>
                                <SelectContent>
                                    {allowedModules.map((m) => (
                                        <SelectItem key={m.id} value={m.id} data-testid={`manual-att-module-opt-${m.code}`}>
                                            {m.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </Field>
                        {!rangeMode && (
                            <Field label="Work date" required>
                                <DateInput
                                    required
                                    value={form.work_date}
                                    onChange={(v) => set("work_date", v)}
                                    data-testid="manual-att-date"
                                />
                            </Field>
                        )}
                    </div>

                    {rangeMode && (
                        <RangeControls
                            range={range}
                            onChange={setRange}
                            invalid={rangeInvalid}
                            testidPrefix="manual-att"
                        />
                    )}

                    <Field label="Employee" required>
                        <Input
                            placeholder="Search by module ID, name, or code…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            data-testid="manual-att-search"
                            className="mb-2"
                        />
                        <Select value={form.employee_id || ""} onValueChange={(v) => set("employee_id", v)}>
                            <SelectTrigger data-testid="manual-att-employee">
                                <SelectValue placeholder={loadingEmps ? "Loading employees…" : "Choose employee"} />
                            </SelectTrigger>
                            <SelectContent className="max-h-72">
                                {filteredEmps.length === 0 ? (
                                    <div className="px-3 py-2 text-xs text-[color:var(--asl-muted)]">
                                        No employees match
                                    </div>
                                ) : (
                                    filteredEmps.map((emp) => (
                                        <SelectItem key={emp.id} value={emp.id} data-testid={`manual-att-emp-opt-${emp.id}`}>
                                            {/* Iter60.p50.1 — Primary identifier is the
                                                Module ID chip. `EMP-####` trailing text
                                                dropped per p49/p50.1 tester audit. */}
                                            <span className="inline-flex items-center gap-2">
                                                <ModuleIdChip value={emp.module_employee_id} testid={`manual-att-emp-mid-${emp.id}`} />
                                                <span className="font-medium">{emp.full_name}</span>
                                            </span>
                                        </SelectItem>
                                    ))
                                )}
                            </SelectContent>
                        </Select>
                        {selectedEmp && (
                            <div className="text-[11px] text-[color:var(--asl-muted)] mt-1">
                                {selectedEmp.job_title} · {selectedEmp.worksite || "—"}
                            </div>
                        )}
                    </Field>

                    <Field label="Project (optional)">
                        {/* Iter67 F1 — Universal ProjectPicker.
                            Empty string on onValueChange represents "no
                            project"; ManualAttendanceDialog historically
                            used `null`, so we normalise here. */}
                        <ProjectPicker
                            projects={projects}
                            value={form.project_id || ""}
                            onValueChange={(v) => set("project_id", v || null)}
                            allowNone
                            testid="manual-att-project"
                            placeholder="No project"
                        />
                    </Field>

                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Clock in">
                            <Input
                                type="time"
                                value={form.clock_in}
                                onChange={(e) => set("clock_in", e.target.value)}
                                data-testid="manual-att-clock-in"
                            />
                        </Field>
                        <Field label="Clock out">
                            <Input
                                type="time"
                                value={form.clock_out}
                                onChange={(e) => set("clock_out", e.target.value)}
                                data-testid="manual-att-clock-out"
                            />
                        </Field>
                        {/* Iter50-followup — user's Status pick wins over the shift-rule
                            computed classification (server PATCHes the daily row after
                            events land / range fan-out). */}
                        <Field label="Status" required>
                            <select
                                value={form.status}
                                onChange={(e) => set("status", e.target.value)}
                                className="h-9 px-3 border rounded-md text-sm bg-white w-full"
                                style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
                                data-testid="manual-status"
                            >
                                {STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </Field>
                    </div>

                    <Field label="Notes">
                        <Textarea
                            rows={2}
                            placeholder="Reason for manual entry (e.g. device offline, missed punch)…"
                            value={form.notes}
                            onChange={(e) => set("notes", e.target.value)}
                            data-testid="manual-att-notes"
                        />
                    </Field>

                    {rangeMode && (
                        <RangeSummary
                            summary={rangeSummary}
                            employees={1}
                            invalid={rangeInvalid}
                            loading={rangePreviewing}
                            testid="manual-att-range-summary"
                        />
                    )}
                    </div>

                    <DialogFooter
                        className="flex-none border-t bg-white px-6 py-3 sm:justify-end"
                        data-testid="manual-att-footer"
                    >
                        <Button
                            type="button"
                            variant="outline"
                            onClick={() => onOpenChange(false)}
                            data-testid="manual-att-cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            className="asl-navy-bg text-white"
                            disabled={submitting || (rangeMode && (rangeInvalid || rangeSummary.total_rows === 0))}
                            data-testid="manual-att-submit"
                        >
                            {submitting
                                ? "Saving…"
                                : rangeMode
                                    ? `Create ${rangeSummary.total_rows || 0} row${rangeSummary.total_rows === 1 ? "" : "s"}`
                                    : "Save attendance"}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
        <AttendanceConflictsDialog
            open={conflictsState.open}
            onOpenChange={(o) => setConflictsState((s) => ({ ...s, open: o }))}
            conflicts={conflictsState.conflicts}
            cleanCount={conflictsState.cleanCount}
            onProceed={async (resolutions) => {
                setConflictsState((s) => ({ ...s, open: false }));
                if (conflictsState.runSave) await conflictsState.runSave(resolutions);
            }}
            onEditExisting={(c) => {
                setConflictsState((s) => ({ ...s, open: false }));
                onOpenChange(false);
                onSaved?.({ editExisting: c });
            }}
            onGoToRecord={(c) => {
                setConflictsState((s) => ({ ...s, open: false }));
                onOpenChange(false);
                onSaved?.({ goToRecord: c });
            }}
        />
        </>
    );
}

function Field({ label, required, children }) {
    return (
        <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wide text-[color:var(--asl-muted)]">
                {label} {required && <span className="asl-crimson-text">*</span>}
            </Label>
            {children}
        </div>
    );
}

// Iter50 — Segmented control shared by both dialogs (single day vs date range).
export function ModeToggle({ value, onChange, testid }) {
    const opts = [
        { v: "single", label: "Single day" },
        { v: "range", label: "Date range" },
    ];
    return (
        <div
            className="inline-flex items-center rounded-md border p-0.5 text-xs"
            style={{ borderColor: "var(--asl-border, #E5E7EB)" }}
            role="tablist"
            data-testid={testid}
        >
            {opts.map((o) => {
                const active = value === o.v;
                return (
                    <button
                        key={o.v}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(o.v)}
                        className={
                            "px-3 py-1.5 rounded transition " +
                            (active
                                ? "asl-navy-bg text-white shadow-sm"
                                : "text-[color:var(--asl-muted)] hover:text-[color:var(--asl-navy)]")
                        }
                        data-testid={`${testid}-${o.v}`}
                    >
                        {o.label}
                    </button>
                );
            })}
        </div>
    );
}

// Iter50 — Shared range picker + skip toggles UI.
export function RangeControls({ range, onChange, invalid, testidPrefix }) {
    const set = (k, v) => onChange({ ...range, [k]: v });
    return (
        <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
                <Field label="From date" required>
                    <DateInput
                        value={range.date_from}
                        onChange={(v) => set("date_from", v)}
                        data-testid={`${testidPrefix}-range-from`}
                    />
                </Field>
                <Field label="To date" required>
                    <DateInput
                        value={range.date_to}
                        onChange={(v) => set("date_to", v)}
                        data-testid={`${testidPrefix}-range-to`}
                    />
                </Field>
            </div>
            {invalid && (
                <div className="text-xs asl-crimson-text" data-testid={`${testidPrefix}-range-error`}>
                    End date must be on or after start date.
                </div>
            )}
            <div className="flex flex-wrap gap-4 text-xs">
                <label
                    className="inline-flex items-center gap-2 cursor-pointer"
                    title="Somalia weekend: Friday only. Saturday is a working day."
                    data-testid={`${testidPrefix}-skip-weekends-label`}
                >
                    <input
                        type="checkbox"
                        checked={!!range.skip_weekends}
                        onChange={(e) => set("skip_weekends", e.target.checked)}
                        data-testid={`${testidPrefix}-skip-weekends`}
                    />
                    <span>Skip weekend</span>
                    <span className="text-[10px] text-[color:var(--asl-muted)]">(Fri)</span>
                </label>
                <label
                    className="inline-flex items-center gap-2 cursor-pointer"
                    title="Uses holidays defined in Shift Rules & Holidays."
                    data-testid={`${testidPrefix}-skip-holidays-label`}
                >
                    <input
                        type="checkbox"
                        checked={!!range.skip_holidays}
                        onChange={(e) => set("skip_holidays", e.target.checked)}
                        data-testid={`${testidPrefix}-skip-holidays`}
                    />
                    <span>Skip holidays</span>
                </label>
            </div>
        </div>
    );
}

// Iter50 — Live summary rendered below range picker.
export function RangeSummary({ summary, employees, invalid, loading, testid }) {
    if (invalid) return null;
    const days = summary?.working_days ?? 0;
    const total = summary?.total_rows ?? 0;
    const skippedEx = summary?.skipped_existing ?? 0;
    const skippedWk = summary?.skipped_weekends ?? 0;
    const skippedHo = summary?.skipped_holidays ?? 0;
    return (
        <div
            className="rounded-md border p-3 text-xs"
            style={{ borderColor: "var(--asl-border, #E5E7EB)", background: "rgba(27, 42, 86, 0.03)" }}
            data-testid={testid}
        >
            <div className="asl-navy-text font-medium">
                {loading ? "Calculating…" : (
                    <>Will create <b>{total}</b> attendance row{total === 1 ? "" : "s"} across <b>{days}</b> working day{days === 1 ? "" : "s"} for <b>{employees}</b> employee{employees === 1 ? "" : "s"}.</>
                )}
            </div>
            {(skippedEx > 0 || skippedWk > 0 || skippedHo > 0) && !loading && (
                <div className="mt-1 text-[color:var(--asl-muted)] text-[11px]">
                    Skipping:{" "}
                    {skippedEx > 0 && <span data-testid={`${testid}-skipped-existing`}>{skippedEx} existing</span>}
                    {skippedWk > 0 && <span data-testid={`${testid}-skipped-weekends`}>{skippedEx > 0 ? " · " : ""}{skippedWk} weekend day{skippedWk === 1 ? "" : "s"}</span>}
                    {skippedHo > 0 && <span data-testid={`${testid}-skipped-holidays`}>{(skippedEx > 0 || skippedWk > 0) ? " · " : ""}{skippedHo} holiday{skippedHo === 1 ? "" : "s"}</span>}
                </div>
            )}
        </div>
    );
}
